import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Link } from "wouter";
import { Helmet } from "react-helmet";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Loader2, ChevronRight, MapPin, BedDouble, Bath, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";

export default function InquiryPage() {
  const [, setLocation] = useLocation();
  const [location] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Parse query parameters from window.location instead of wouter location
  const params = new URLSearchParams(window.location.search);
  
  const propertyId = Number(params.get("propertyId") || "0");
  const linkId = Number(params.get("linkId") || "0");
  const agentId = Number(params.get("agentId") || "0");
  const price = Number(params.get("price") || "0");
  const linkCode = params.get("linkCode") || "";
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: user?.fullName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    checkIn: null as Date | null,
    checkOut: null as Date | null,
    adults: 1,
    children: 0,
    message: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [property, setProperty] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch property data
  useEffect(() => {
    const fetchProperty = async () => {
      if (!propertyId && !linkCode) {
        toast({
          title: "Invalid property",
          description: "No property information provided.",
          variant: "destructive",
        });
        setLocation("/properties");
        return;
      }

      try {
        setIsLoading(true);
        
        // If we have a link code, fetch through the property link endpoint
        let response;
        if (linkCode) {
          response = await apiRequest('GET', `property-links/${linkCode}`);
          // if (!response.ok) {
          //   throw new Error("Failed to fetch property link");
          // }
          // const data = await response.json();
          const data = response;
          if (!data.property) {
            throw new Error("Property not found in link data");
          }
          setProperty(data.property);
        } else {
          response = await apiRequest('GET', `properties/${propertyId}`);
          // if (!response.ok) {
          //   throw new Error("Failed to fetch property");
          // }
          // const data = await response.json();
          const data =  response;
          setProperty(data);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load property details. Please try again.",
          variant: "destructive",
        });
        setLocation("/properties");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [propertyId, linkCode, setLocation, toast]);
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  // Handle number input changes
  const handleNumberChange = (name: string, value: number) => {
    if (value >= 0) {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!property) return;
    
    try {
      setIsSubmitting(true);
      
      const inquiryData = {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        propertyId: property.id,
        checkIn: formData.checkIn?.toISOString(),
        checkOut: formData.checkOut?.toISOString(),
        agentLinkId: linkId || undefined,
        agentId: agentId || undefined,
        price: price || undefined,
        linkCode: linkCode || undefined,
      };
      
      const response = await apiRequest("POST", "inquiries", inquiryData);
      
      if (response.ok) {
        setSubmitted(true);
        toast({
          title: "Inquiry submitted",
          description: "We'll get back to you shortly.",
        });
      } else {
        throw new Error("Failed to submit inquiry");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit inquiry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // If inquiry was submitted successfully
  if (submitted) {
    return (
      <Layout>
        <div className="container py-8 md:py-12">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="pt-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4">Thank You!</h1>
                <p className="text-muted-foreground mb-6">
                  Your inquiry has been submitted successfully. We'll get back to you shortly.
                </p>
                <Button asChild>
                  <Link href="/properties">Browse More Properties</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <Helmet>
        <title>Property Inquiry | StayMiddle</title>
      </Helmet>
      <div className="container py-8 md:py-12">
        {/* Breadcrumbs */}
        <div className="flex items-center text-sm text-muted-foreground mb-6">
          <Link href="/">
            <span className="hover:text-primary cursor-pointer">Home</span>
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link href="/properties">
            <span className="hover:text-primary cursor-pointer">Properties</span>
          </Link>
          {property && (
            <>
              <ChevronRight className="h-4 w-4 mx-2" />
              <span>Inquiry</span>
            </>
          )}
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center h-60">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : property ? (
          <>
            {/* Page Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold mb-2">Request Information</h1>
              <p className="text-muted-foreground">Submit an inquiry about this property</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Property Information */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xl">{property.title}</CardTitle>
                  <CardDescription className="flex items-center mt-1">
                    <MapPin className="h-4 w-4 mr-1" /> {property.area}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-4">
                  <div className="aspect-[16/9] overflow-hidden rounded-md mb-4">
                    {property.images && property.images[0] ? (
                      <img
                        src={property.images[0].imageUrl}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-muted-foreground">No image available</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-between text-sm mb-4">
                    <div className="flex items-center">
                      <BedDouble className="h-4 w-4 mr-1" />
                      <span>{property.bedrooms} Beds</span>
                    </div>
                    <div className="flex items-center">
                      <Bath className="h-4 w-4 mr-1" />
                      <span>{Number(property.bathrooms).toFixed(1)} Baths</span>
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      <span>Sleeps {property.maxGuests}</span>
                    </div>
                  </div>
                  
                  <Separator className="my-4" />
                  
                  <p className="text-sm text-muted-foreground mb-4">
                    {property.description}
                  </p>
                  
                  {price > 0 && (
                    <div className="text-right">
                      <span className="text-2xl font-bold">{formatCurrency(price)}</span>
                      <span className="text-muted-foreground"> / night</span>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Inquiry Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>
                    Please provide your details and we'll get back to you shortly
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name</Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="adults">Adults</Label>
                        <Input
                          id="adults"
                          name="adults"
                          type="number"
                          min="1"
                          value={formData.adults}
                          onChange={(e) => handleNumberChange("adults", parseInt(e.target.value))}
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="children">Children</Label>
                        <Input
                          id="children"
                          name="children"
                          type="number"
                          min="0"
                          value={formData.children}
                          onChange={(e) => handleNumberChange("children", parseInt(e.target.value))}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Additional Information</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your trip, any special requirements, etc."
                        className="min-h-[120px]"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                      ) : (
                        "Submit Inquiry"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold mb-4">Property Not Found</h1>
            <p className="text-muted-foreground mb-8">We couldn't find the property you're looking for.</p>
            <Button asChild>
              <Link href="/properties">Browse Properties</Link>
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}